/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../../theme/theme"
import { css } from "@emotion/react"
import {
    Button, CircularProgress, Snackbar, Alert,
    Dialog, DialogContent, DialogActions,
    FormControlLabel, Checkbox, Typography
} from "@mui/material"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import SaveIcon from '@mui/icons-material/Save'
import CheckIcon from '@mui/icons-material/Check'
import ScheduleIcon from '@mui/icons-material/Schedule'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { mobileCss } from "../../../util/isMobile"
import { MyButton } from "../../../components/button/MyButton"

const DISMISS_SAVE_DIALOG_KEY = 'editor-dismiss-save-dialog'

const SaveButtonStyle = withMyTheme((theme) => css`
    padding: 10px 24px;
    font-size: 1rem;
    border-radius: 8px;
    text-transform: none;
    font-weight: 600;
    min-width: 120px;
    
    ${mobileCss(`
        padding: 8px 16px;
        font-size: 0.9rem;
        min-width: 100px;
    `)}
`)

const DialogIconStyle = withMyTheme((theme) => css`
    font-size: 3rem;
    color: ${theme.palette.primary.main};
`)

const DialogContentContainerStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1.5rem;
    padding: 2rem 2rem 0.5rem;
`)

const DialogTitleTextStyle = withMyTheme((theme) => css`
    font-family: ${theme.typography.body1.fontFamily};
    font-size: 1.3rem;
    font-weight: 600;
    color: ${theme.palette.text.primary};
`)

const DialogDescStyle = withMyTheme((theme) => css`
    font-family: ${theme.typography.body1.fontFamily};
    font-size: 1rem;
    color: ${theme.palette.text.primary};
    line-height: 1.6;
    opacity: 0.85;
`)

const CheckboxLabelStyle = withMyTheme((theme) => css`
    .MuiFormControlLabel-label {
        font-family: ${theme.typography.body1.fontFamily};
        font-size: 0.9rem;
        color: ${theme.palette.text.primary};
        opacity: 0.7;
    }
`)

const CheckboxStyle = withMyTheme((theme) => css`
    &.Mui-checked {
        color: ${theme.palette.primary.main};
    }
`)

const DialogActionsContainerStyle = withMyTheme(() => css`
    justify-content: center;
    padding: 1rem 2rem 1.5rem;
`)

const SaveAreaStyle = withMyTheme(() => css`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    ${mobileCss(`
        gap: 0.5rem;
    `)}
`)

const LocalSaveHintStyle = withMyTheme((theme) => css`
    display: flex;
    align-items: center;
    gap: 0.3rem;
    font-family: ${theme.typography.body1.fontFamily};
    font-size: 0.75rem;
    color: ${theme.palette.text.secondary};
    opacity: 0.7;
    max-width: 140px;
    line-height: 1.3;
    
    ${mobileCss(`
        font-size: 0.65rem;
        max-width: 110px;
    `)}
`)

const HintIconStyle = withMyTheme((theme) => css`
    font-size: 0.9rem;
    color: ${theme.palette.text.secondary};
    opacity: 0.6;
    flex-shrink: 0;
`)

interface EditorSaveButtonProps {
    hasUnsavedChanges: boolean;
    onSave: () => Promise<void>;
}

export const EditorSaveButton = ({ hasUnsavedChanges, onSave }: EditorSaveButtonProps) => {
    const { t } = useTranslation()
    const [saving, setSaving] = useState(false)
    const [showSuccess, setShowSuccess] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [dialogOpen, setDialogOpen] = useState(false)
    const [dontShowAgain, setDontShowAgain] = useState(false)

    const handleSave = async () => {
        setSaving(true)
        setError(null)
        
        try {
            await onSave()
            setShowSuccess(true)
            setTimeout(() => setShowSuccess(false), 3000)

            const dismissed = localStorage.getItem(DISMISS_SAVE_DIALOG_KEY)
            if (dismissed !== 'true') {
                setDialogOpen(true)
            }
        } catch (err) {
            console.error('Failed to save:', err)
            setError(err instanceof Error ? err.message : 'Failed to save changes')
        } finally {
            setSaving(false)
        }
    }

    const handleCloseDialog = () => {
        if (dontShowAgain) {
            localStorage.setItem(DISMISS_SAVE_DIALOG_KEY, 'true')
        }
        setDialogOpen(false)
        setDontShowAgain(false)
    }

    const handleCloseError = () => {
        setError(null)
    }

    return (
        <>
            <div css={SaveAreaStyle}>
                <span css={LocalSaveHintStyle}>
                    <InfoOutlinedIcon css={HintIconStyle} />
                    {t('editor.localSaveHint')}
                </span>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!hasUnsavedChanges || saving}
                    css={SaveButtonStyle}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : (showSuccess ? <CheckIcon /> : <SaveIcon />)}
                >
                    {saving ? t('editor.saving') : (showSuccess ? t('editor.saved') : t('editor.save'))}
                </Button>
            </div>

            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                PaperProps={{ sx: { borderRadius: '16px', maxWidth: '440px' } }}
            >
                <DialogContent>
                    <div css={DialogContentContainerStyle}>
                        <ScheduleIcon css={DialogIconStyle} />
                        <Typography css={DialogTitleTextStyle}>
                            {t('editor.saveDialog.title')}
                        </Typography>
                        <Typography css={DialogDescStyle} dangerouslySetInnerHTML={{ __html: t('editor.saveDialog.description') }} />
                        <FormControlLabel
                            css={CheckboxLabelStyle}
                            control={
                                <Checkbox
                                    css={CheckboxStyle}
                                    checked={dontShowAgain}
                                    onChange={(e) => setDontShowAgain(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={t('editor.saveDialog.dontShowAgain')}
                        />
                    </div>
                </DialogContent>
                <DialogActions css={DialogActionsContainerStyle}>
                    <MyButton
                        variant="primary"
                        text={t('editor.saveDialog.ok')}
                        onClick={handleCloseDialog}
                    />
                </DialogActions>
            </Dialog>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </>
    )
}
